<template>
    <card title="Label Templates">
        <b-table :items="templates" :fields="tableFields">
            <template #cell(active)="{ value }">
                {{ value ? "✅" : "❌" }}
            </template>
            <template #cell(type)="{ value }">
                {{ LabelTemplateType[value] }}
            </template>
            <template #cell(dpi)="{ value, item }">
                {{ value }}
                <i v-if="resolutionSelectsTemplate(item.type) && !isPrintableDpi(value)"
                    class="fas fa-exclamation-triangle text-warning ml-1"
                    :title="`Labels are only printed at ${printableDpiList} DPI, so this template is never used for a real label.`" />
            </template>
            <template #cell(stores)="{ value }">
                <b-badge v-if="!value || !value.length" variant="primary">Any Store</b-badge>
                <template v-else>
                    <b-badge v-for="v in value" variant="info" :key="v.id">{{ v.name }}</b-badge>
                </template>
            </template>
            <template #cell(programs)="{ value }">
                <b-badge v-if="!value || !value.length" variant="primary">Any Program</b-badge>
                <template v-else>
                    <b-badge v-for="v in value" variant="info" :key="v.id">{{ v.name }}</b-badge>
                </template>
            </template>
            <template #cell(states)="{ value }">
                <b-badge v-if="!value || !value.length" variant="primary">Any State</b-badge>
                <template v-else>
                    <b-badge v-for="v in value" variant="info" :key="v">{{ v }}</b-badge>
                </template>
            </template>
            <template #cell(actions)="data">
                <div class="table-icon-actions">
                    <a href="#" size="sm" variant="primary" @click.prevent="loadTemplate(data.item)">
                        <b-icon icon="pencil-square"></b-icon>
                    </a>
                </div>
            </template>
        </b-table>

        <BottomPagerBar hide-pager>
            <template #left>
                <b-button size="sm" variant="primary" @click="newTemplate">
                    New Template
                </b-button>
                <b-button size="sm" variant="info" @click="showTemplateTester = true">
                    Template Tester
                </b-button>
            </template>
        </BottomPagerBar>

        <b-modal :visible="!!activeTemplate" title="Template" @hidden="activeTemplate = null" no-fade size="xl"
            ok-title="Save Template" @ok="saveTemplate">
            <template #modal-header="{ close }">
                <div class="w-100 d-flex flex-row align-items-baseline">
                    <h5 class="modal-title">Template</h5>
                    <div class="ml-auto d-flex flex-row align-items-end">
                        <history-modal v-if="activeTemplate?.id" v-model="activeTemplate" class="mx-4"/>
                        <button type="button" class="close" @click="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
            </template>
            <b-row v-if="activeTemplate">

                <b-col>
                    <b-form-group>
                        <div class="d-flex align-items-center">
                            <label class="mb-0 mr-1">Active</label>
                            <b-checkbox switch v-model="activeTemplate.active" />
                        </div>
                    </b-form-group>
                    <b-form-group label="Name">
                        <b-input v-model="activeTemplate.name" />
                    </b-form-group>
                    <b-form-group>
                        <EnumSelector v-model="activeTemplate.type" :enum-codes="LabelTemplateType" label="Type"
                            required />
                    </b-form-group>
                    <b-form-group>
                        <template #label>
                            Printer DPI
                            <HelpPopup>
                                <p>The printer resolution this template's ZPL is drawn for.</p>
                                <p>
                                    ZPL positions everything in <strong>dots</strong> rather than inches, so
                                    the same template renders at a different physical size on every
                                    resolution. It only prints at the intended size on a printer of this DPI.
                                </p>
                                <p>
                                    Scalable tokens such as
                                    <code>{{ "\{\{ScalableDirections\}\}" }}</code> are rasterized here at
                                    this resolution, so their text lands at the intended point size on that
                                    printer.
                                </p>
                                <p>
                                    Changing this does <strong>not</strong> convert the template. To target a
                                    different printer, scale every <code>^FO</code>, <code>^A0</code> and
                                    <code>^GB</code> coordinate to match.
                                </p>
                            </HelpPopup>
                        </template>
                        <b-select v-model="activeTemplate.dpi" :options="dpiOptions" />
                        <b-form-text
                            v-if="resolutionSelectsTemplate(activeTemplate.type) && !isPrintableDpi(activeTemplate.dpi)"
                            class="text-warning">
                            <i class="fas fa-exclamation-triangle" />
                            Labels are only printed at {{ printableDpiList }} DPI. A template saved at this
                            resolution can be drawn and previewed here, but the filling screen cannot select
                            it, so it will never be used for a real label.
                        </b-form-text>
                    </b-form-group>
                    <b-form-group label="Limit to Stores">
                        <multiselect v-model="activeTemplate.stores" :options="stores" :close-on-select="false"
                            :clear-on-select="false" label="name" track-by="id" multiple />
                    </b-form-group>
                    <b-form-group label="Limit to Programs">
                        <multiselect v-model="activeTemplate.programs" :options="programs" :close-on-select="false"
                            :clear-on-select="false" label="name" track-by="id" multiple />
                    </b-form-group>
                    <b-form-group label="Limit to States">
                        <multiselect v-model="activeTemplate.states" :options="states" :close-on-select="false"
                            :clear-on-select="false" multiple />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-popover target="template-zpl" triggers="focus" placement="left"
                        custom-class="text-message-template label-token-popover">
                        <div v-if="availableTokens.length">
                            <h6>Available Tokens <small class="text-muted">Click to add</small></h6>
                            <div class="tokens">
                                <code v-for="tkn in availableTokens" :key="tkn" @click="addToken(tkn)">
                            {{ "\{\{" + tkn + "\}\}" }}
                        </code>
                            </div>
                        </div>
                        <h6 class="m-0 p-0">Limit Token Value Length</h6>
                        To limit the maximium length of a token replacement value, include a colon plus the maximium
                        length:

                        <code>{{ "\{\{GenericForLong:20\}\}" }}</code>
                    </b-popover>
                    <b-form-group label="Template ZPL">
                        <b-textarea ref="templateZpl" id="template-zpl" class="zpl-editor"
                            v-model="activeTemplate.template" />
                    </b-form-group>
                    <b-button size="sm" @click="getDefaultTemplate" block>
                        Load System Default Template
                    </b-button>
                </b-col>
            </b-row>

            <template #modal-footer="{ ok, close }">
                <b-button @click="close">Cancel</b-button>
                <div>
                    <b-button @click="previewActiveTemplate" variant="primary" class="mr-1">Preview...</b-button>
                    <b-button @click="ok" variant="success">Save Template</b-button>
                </div>
            </template>
        </b-modal>

        <b-modal v-model="showTemplateTester" :title="previewFromEditor ? 'Preview Template' : 'Template Tester'"
            size="xl" hide-footer no-fade @hidden="previewFromEditor = false">
            <b-row>
                <b-col cols="12" xl="4">
                    <b-form-group v-if="!previewFromEditor">
                        <EnumSelector v-model="previewTemplate.type" :enum-codes="LabelTemplateType" label="Type"
                            required />
                    </b-form-group>

                    <SearchComponent v-model="previewTemplate.store" label="Store" :search-u-r-l="`/Store`"
                        :prepopulate="true" :clearable="false" />

                    <SearchComponent v-model="previewTemplate.program" label="Program" :search-u-r-l="`/Program`"
                        :prepopulate="true" :clearable="false" />

                    <b-form-group label="State">
                        <b-select v-model="previewTemplate.state" :options="states" />
                    </b-form-group>

                    <hr>

                    <SearchComponent v-model="previewTemplate.drug" :label="`Drug`"
                        :params="{ onlyActive: false, allSources: false }"
                        :params-in-get-by-id="{ drugSource: previewTemplate.drug?.source }"
                        :search-u-r-l="`/Drug`" />

                    <b-form-group label="Directions">
                        <b-textarea v-model="previewTemplate.directions" rows="1" class="textarea-autosize"
                                    placeholder="Leave blank to use sample directions" />
                    </b-form-group>

                    <!-- <b-form-group label="Language Code">
                        <b-input v-model="previewTemplate.languageCode" />
                    </b-form-group> -->

                    <hr>

                    <b-form-group v-if="!previewFromEditor">
                        <div class="d-flex align-items-center justify-content-between">
                            <label class="mb-0">Include Inactive Templates</label>
                            <!-- A label-less switch still reserves its label's padding, which would
                                 leave the toggle sitting inboard of the inputs above and below. -->
                            <b-check switch class="mr-n2" v-model="previewTemplate.all" />
                        </div>
                    </b-form-group>

                    <b-form-group v-if="!previewFromEditor" label="Use Specific Template">
                        <b-select v-model="previewTemplate.templateId" :options="templateOptions" />
                    </b-form-group>

                    <b-form-group label="Partial Fill Mode">
                        <b-select v-model="previewTemplate.partialFillMode"
                                  :options="[
                                      { value: 'none', text: 'None' },
                                      { value: 'partial', text: 'Partial Fill' },
                                      { value: 'completion', text: 'Completion Fill' }
                                  ]" />
                    </b-form-group>

                    <b-form-group v-if="previewTemplate.partialFillMode !== 'none'"
                                  label="Original Prescribed Qty">
                        <b-input type="number" v-model.number="previewTemplate.originalPrescribedQty" />
                    </b-form-group>

                    <hr>

                    <b-form-group label="Label Size">
                        <b-select v-model="labelSizePreset" :options="labelSizeOptions" />
                    </b-form-group>

                    <b-row v-if="labelSizePreset === 'custom'">
                        <b-col>
                            <b-form-group label="Width (in)">
                                <b-input type="number" step="0.05" min="0.1"
                                         v-model.number="customLabelWidth" />
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group label="Height (in)">
                                <b-input type="number" step="0.05" min="0.1"
                                         v-model.number="customLabelHeight" />
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <b-form-group label="Printer DPI">
                        <b-select v-model="previewDpi" :options="dpiOptions" />
                    </b-form-group>

                    <b-button block variant="primary" @click="getPreview">Preview Label</b-button>

                    <hr>

                    <b-alert :show="!hasSupportedClientService" variant="warning" class="mb-2">
                        <i class="fa fa-warning"></i>
                        Scriptly Client Service not detected. Label printing will be unavailable.
                    </b-alert>

                    <b-form-group label="Label Printer">
                        <b-dropdown :text="prettyPrinterName(activeLocalPrinter)" variant="outline-primary" block>
                            <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                :active="p.name === activeLocalPrinter?.name" @click="activeLocalPrinter = p">
                                <i class="fa fa-print"></i>
                                {{ prettyPrinterName(p) }}
                            </b-dropdown-item-button>
                            <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                <i class="fa fa-info-circle" />
                                To print to local printers, install<br>the Scriptly Client Service
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-form-group>

                    <b-button block variant="success" :disabled="!canPrint" @click="printPreview">
                        Print Label
                    </b-button>
                </b-col>
                <b-col cols="12" xl="8" class="preview-pane">
                    <b-overlay :show="loadingPreview">
                        <template v-if="previewImageUrl || previewImageError">
                            <img v-if="previewImageUrl" :src="previewImageUrl" />
                            <h5 v-else>Failed to load preview</h5>
                            <hr>
                            <strong>Template:</strong>
                            {{ previewFromEditor ? editorPreviewTemplateName + ' (unsaved)' : previewContent?.template?.name }}
                        </template>
                        <template v-if="false">
                            <hr>
                            {{ previewContent }}
                        </template>
                    </b-overlay>
                </b-col>
            </b-row>
        </b-modal>
    </card>
</template>

<script lang="ts">
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import { Store } from '@/models/Store';
import Axios from 'axios';
import { Component, Mixins, Ref, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { Program } from '@/models/Program';
import { ValidValues } from '@/data/ValidValues';
import { toRaw } from 'vue';
import { LabelTemplate, LabelTemplateType } from '@/models/LabelTemplate';
import EnumSelector from '@/components/EnumSelector.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import { Drug } from '@/models/Drug/Drug';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import HistoryModal from '@/components/Modals/HistoryModal.vue';
import HelpPopup from '@/components/HelpPopup.vue';
import ClientServiceWorkerMixin from '@/mixins/ClientServiceWorkerMixin';
import PrintService, { PrintDevice, RawPrintRequest } from '@/services/PrintService';
import {
    DEFAULT_LABEL_DPI,
    authorableDpiSelectOptions,
    isAuthorableLabelDpi,
    isSupportedLabelDpi,
    printableLabelDpiList,
    toDpmm
} from '@/util/labelDpi';

/** The shared worker's answer to a raw print. Either success or error is set, never both. */
interface RawPrintAck {
    jobId?: string | null;
    success?: boolean;
    error?: string;
    /** Set when nothing was sent to the client service, so retrying over HTTP can't duplicate. */
    shouldFallback?: boolean;
}

/**
 * How long a print waits for the worker to confirm the client service took the job. Generous
 * because the reply follows the label being spooled, and timing out early would report a failure
 * for a label that did print.
 */
const printAckTimeoutMs = 15000;

const labelSizePresets = [
    { value: "4x2", text: '4" x 2" (bottle label)', width: 4, height: 2 },
    { value: "4x3", text: '4" x 3"', width: 4, height: 3 },
    { value: "4x6", text: '4" x 6" (shipping)', width: 4, height: 6 },
    { value: "3x2", text: '3" x 2"', width: 3, height: 2 },
    { value: "2.25x1.25", text: '2.25" x 1.25"', width: 2.25, height: 1.25 }
];

const labelSizeSelectOptions = [...labelSizePresets, { value: "custom", text: "Custom..." }];

// Reproduces what the tester rendered before size was selectable.
const defaultLabelSize = labelSizePresets[0];

const printerStorageKey = "active-label-tester-printer";
const labelSizeStorageKey = "label-tester-label-size";
const dpiStorageKey = "label-tester-dpi";

const baseTokensBottleLabels = [
    "BrandingName",
    "BrandingPhone",
    "StoreAddress1",
    "StoreAddress2",
    "StoreCityStateZip",
    "StoreID",
    "RxNumber",
    "RfNumber",
    "RfNumberDisplay",
    "PrescriberDisplayName",
    "PracticeName",
    "PrescriberOrPracticeName",
    "PrescriberAddress",
    "PrescriberAddress1",
    "PrescriberAddress2",
    "PrescriberAddressCity",
    "PrescriberAddressState",
    "PrescriberAddressZip",
    "PrescriberNPI",
    "PrescriberDEA",
    "PatientLastName",
    "PatientFirstName",
    "PatientMiddleName",
    "FillDate",
    "Dispensed",
    "UnitOfMeasure",
    "DrugName",
    "DrugNameLong",
    "Directions",
    "GenericFor",
    "GenericForLong",
    "DrugExpirationDate",
    "RefillsRemaining",
    "ExpirationDate",
    "LotNumber",
    "Marketer",
    "PharmacistName",
    "PharmacistFirstInitialLastName",
    "PharmacistFullName",
    "TechInitials",
    "ChildCaps",
    "Warning1",
    "Warning2",
    "Warning3",
    "Warning4",
    "Warning5",
    "LabelCustomization",
    "WrittenDate",
    "PatientAddress1",
    "PatientAddress2",
    "PatientAddressCity",
    "PatientAddressState",
    "PatientAddressZip",
    "NDC10",
    "NDC11",
    "ScalableDirections",
    "ScalableWarning1",
    "ScalableWarning2",
    "ScalableWarning3",
    "ScalableWarning4",
    "ScalableWarning5",
    "PartialFillIndicator",
    "OriginalPrescribedQty",
    "IsPartialFill",
    "IsCompletionFill"
];

const tokensByType = {
    0: [...baseTokensBottleLabels],
    1: [...baseTokensBottleLabels, "Url"]
}

@Component({
    name: "LabelTemplatesPage",
    components: {
        BottomPagerBar,
        Multiselect,
        EnumSelector,
        SearchComponent,
        HistoryModal,
        HelpPopup
    }
})
export default class LabelTemplatesPage extends Mixins(ClientServiceWorkerMixin) {

    protected stores: Array<Store> = [];
    protected programs: Array<Program> = [];
    protected states = ValidValues.addressStates;

    protected LabelTemplateType = LabelTemplateType;

    protected showTemplateTester = false;

    // When true, the tester was opened from the editor to preview the current unsaved ZPL.
    protected previewFromEditor = false;
    private editorPreviewZpl = "";
    // Name of the template being previewed from the editor, shown in the tester as "<name> (unsaved)".
    private editorPreviewTemplateName = "";

    @Ref("templateZpl") protected templateZpl: any;

    private templates: LabelTemplate[] = [];
    get tableFields() {
        return [
            { key: "id" },
            { key: "active" },
            { key: "name" },
            { key: "type" },
            { key: "dpi", label: "DPI" },
            { key: "stores" },
            { key: "programs" },
            { key: "states" },
            { key: "actions", label: "" }
        ]
    }

    private activeTemplate: LabelTemplate | null = null;
    private previewTemplate = {
        type: LabelTemplateType.bottleLabel,
        store: new Store(),
        program: new Program(),
        state: this.states[0],
        drug: new Drug(),
        languageCode: "en-US",
        all: false,
        templateId: null,
        partialFillMode: 'none' as 'none' | 'partial' | 'completion',
        originalPrescribedQty: 90,
        directions: ""
    };

    protected labelSizePreset = defaultLabelSize.value;
    protected customLabelWidth = defaultLabelSize.width;
    protected customLabelHeight = defaultLabelSize.height;
    protected previewDpi: number = DEFAULT_LABEL_DPI;
    // Set while the DPI is being taken from a template rather than chosen in the DPI dropdown.
    private adoptingTemplateDpi = false;

    protected localPrinters: PrintDevice[] = [];
    protected activeLocalPrinter: PrintDevice | null = null;
    protected hasSupportedClientService = false;
    protected printing = false;

    get labelSizeOptions() {
        return labelSizeSelectOptions;
    }

    get dpiOptions() {
        return authorableDpiSelectOptions;
    }

    /**
     * Whether a template drawn at this resolution can ever reach a printer. Templates can be authored
     * at more resolutions than labels can be printed at — the preview renderer accepts any resolution
     * expressed in whole dots per millimetre, while the filling screen only offers those with a
     * built-in drawing behind them. Template lookup filters on resolution exactly, so one saved
     * outside that set is simply never found.
     */
    isPrintableDpi(dpi?: number): boolean {
        return isSupportedLabelDpi(Number(dpi));
    }

    /**
     * Whether resolution takes any part in choosing a template of this type. Only bottle labels
     * are chosen that way — they are the types the filling screen's resolution selector applies to,
     * and the only ones with a built-in drawing per resolution behind them. Every other type is
     * resolved without regard to resolution, so telling its author which resolutions reach a
     * printer would describe a rule their template is not subject to.
     */
    resolutionSelectsTemplate(type?: LabelTemplateType): boolean {
        return type === LabelTemplateType.bottleLabel || type === LabelTemplateType.bottleLabelQrCode;
    }

    get printableDpiList(): string {
        return printableLabelDpiList();
    }

    get selectedLabelSize(): { width: number, height: number } {
        if (this.labelSizePreset === "custom") {
            return { width: this.customLabelWidth, height: this.customLabelHeight };
        }
        const preset = labelSizePresets.find(p => p.value === this.labelSizePreset) ?? defaultLabelSize;
        return { width: preset.width, height: preset.height };
    }

    // Watched instead of selectedLabelSize itself: Vue 2 re-fires a watcher on any object-valued
    // computed whether or not it changed, so watching the object re-rendered on every keystroke.
    get selectedLabelSizeKey() {
        const { width, height } = this.selectedLabelSize;
        return `${width}x${height}`;
    }

    get selectedDpmm() {
        return toDpmm(this.previewDpi);
    }

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    // Printing does not require a preview first: it generates the ZPL itself when none has been
    // rendered yet, so the only prerequisites are somewhere to send it and nothing already in flight.
    get canPrint() {
        return this.hasSupportedClientService
            && !!this.activeLocalPrinter
            && !this.printing
            && !this.loadingPreview;
    }

    get availableTokens() {
        if (!this.activeTemplate) return [];
        return tokensByType[this.activeTemplate.type];
    }

    async mounted() {
        try {
            const stores = await Axios.get<Store[]>(`/Store/`);
            this.stores = stores.data.map(s => new Store(s));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

        try {
            const programs = await Axios.get<Program[]>(`/Program/`);
            this.programs = programs.data.map(s => new Program(s));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

        this.previewTemplate.store = this.stores[0];
        this.previewTemplate.program = this.programs[0];

        this.restorePreviewSettings();

        this.getTemplates();
        this.getInitialDrugForPreview();
        this.getSupportedClientService();
        this.getLocalPrinters();
    }

    beforeDestroy() {
        // Retires any in-flight render too, so a response arriving after teardown can't mint an
        // object URL that nothing is left to revoke.
        this.previewRenderSeq++;
        this.revokePreviewImage();
    }

    restorePreviewSettings() {
        // Sizes persist as "WxH", which is also how the presets are keyed — anything that isn't a
        // known preset restores as a custom size.
        const savedSize = localStorage.getItem(labelSizeStorageKey) ?? "";
        const [savedWidth, savedHeight] = savedSize.split("x").map(Number);
        if (savedWidth > 0 && savedHeight > 0) {
            this.customLabelWidth = savedWidth;
            this.customLabelHeight = savedHeight;
            this.labelSizePreset = labelSizePresets.some(p => p.value === savedSize) ? savedSize : "custom";
        }

        const savedDpi = Number(localStorage.getItem(dpiStorageKey));
        if (isAuthorableLabelDpi(savedDpi)) {
            this.previewDpi = savedDpi;
        }
    }

    async getTemplates() {
        try {
            const resp = await this.$http.get<LabelTemplate[]>("/LabelTemplate");
            this.templates = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getInitialDrugForPreview() {
        try {
            const resp = await this.$http.get<Drug[]>("/Drug/?allSources=false&searchTerm=test");
            this.previewTemplate.drug = Object.assign(new Drug(), resp.data[0]);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async loadTemplate(item: any) {
        this.activeTemplate = new LabelTemplate(structuredClone(toRaw(item)));
    }

    newTemplate() {
        this.activeTemplate = new LabelTemplate({
            id: 0,
            active: true,
            name: "",
            type: LabelTemplateType.bottleLabel,
            template: "",
            dpi: DEFAULT_LABEL_DPI
        });
    }

    async saveTemplate(e: any) {
        e?.preventDefault();

        try {
            await this.$http.post("/LabelTemplate", this.activeTemplate);
            this.activeTemplate = null;
            this.getTemplates();
            this.$notification(NotificationOptions.successSaveNotificationPreset("Label Template"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    private previewContent: any = null;
    private loadingPreview = false;
    private previewImageError = false;
    private previewImageUrl = "";
    private previewRenderSeq = 0;

    // Opens the tester to preview the current unsaved ZPL from the editor modal.
    previewActiveTemplate() {
        if (!this.activeTemplate) return;
        this.previewFromEditor = true;
        this.previewTemplate.type = this.activeTemplate.type;
        this.editorPreviewZpl = this.activeTemplate.template;
        this.editorPreviewTemplateName = this.activeTemplate.name;
        this.adoptTemplateDpi(this.activeTemplate.dpi);
        this.showTemplateTester = true;
        // Render the current unsaved ZPL immediately so the tester opens with a preview already shown.
        // The server defaults the drug when none is selected, so this renders even without a drug loaded.
        this.$nextTick(() => this.getPreview());
    }

    async getPreview() {
        try {
            await this.fetchPreview();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    /**
     * Fetches the ZPL for the current settings and renders it. Throws rather than notifying, so a
     * caller that has its own error handling — printing — doesn't raise a second toast for one
     * failure.
     */
    private async fetchPreview() {
        try {
            this.loadingPreview = true;
            const partialFillMode = this.previewTemplate.partialFillMode !== 'none'
                ? this.previewTemplate.partialFillMode : undefined;
            const originalPrescribedQty = this.previewTemplate.partialFillMode !== 'none'
                ? this.previewTemplate.originalPrescribedQty : undefined;

            // Blank directions fall back to the API's sample text.
            const directions = this.previewTemplate.directions?.trim() || undefined;

            let resp;
            if (this.previewFromEditor) {
                // Preview the unsaved ZPL via POST so the editor's current edits are rendered as-is.
                resp = await this.$http.post("/LabelTemplate/preview", {
                    type: this.previewTemplate.type,
                    storeId: this.previewTemplate.store?.id,
                    programId: this.previewTemplate.program?.id,
                    state: this.previewTemplate.state,
                    packageId: this.previewTemplate.drug?.packageID,
                    languageCode: this.previewTemplate.languageCode,
                    template: this.editorPreviewZpl,
                    partialFillMode,
                    originalPrescribedQty,
                    directions,
                    dpi: this.previewDpi
                });
            } else {
                resp = await this.$http.get("/LabelTemplate/preview", {
                    params: {
                        type: this.previewTemplate.type,
                        storeId: this.previewTemplate.store?.id,
                        programId: this.previewTemplate.program?.id,
                        state: this.previewTemplate.state,
                        packageId: this.previewTemplate.drug?.packageID,
                        languageCode: this.previewTemplate.languageCode,
                        includeInactive: this.previewTemplate.all,
                        templateId: this.previewTemplate.templateId,
                        partialFillMode,
                        originalPrescribedQty,
                        directions,
                        dpi: this.previewDpi
                    }
                });
            }
            this.previewContent = resp.data;
            await this.renderPreviewImage();
        } finally {
            this.loadingPreview = false;
        }
    }

    /**
     * Renders the current ZPL to an image via Labelary.
     *
     * Posted rather than sent as a URL path because rasterized scalable text embeds a bitmap as a
     * ^GF block, which pushes the ZPL well past what fits in a URL. Uses fetch instead of the
     * shared Axios instance so the app's auth interceptors don't attach a bearer token to a
     * third-party request.
     */
    async renderPreviewImage() {
        // Changing size or DPI can start a render while an earlier one is still in flight. Only
        // the newest render may publish its result, so a slow earlier response can't overwrite a
        // newer image or leak its object URL.
        const renderSeq = ++this.previewRenderSeq;

        const zpl = this.previewContent?.content;
        const { width, height } = this.selectedLabelSize;

        // Nothing to render yet is a blank slate, not a failure; ZPL we can't size is a failure.
        if (!zpl) {
            this.revokePreviewImage();
            this.previewImageError = false;
            return;
        }

        if (!(width > 0) || !(height > 0)) {
            this.revokePreviewImage();
            this.previewImageError = true;
            return;
        }

        try {
            // The content type is declared explicitly because fetch infers text/plain from a
            // string body, which the render service rejects with a 415. It reads the raw body
            // rather than form-decoding it, so the ZPL is sent verbatim.
            const resp = await fetch(
                `https://api.labelary.com/v1/printers/${this.selectedDpmm}dpmm/labels/${width}x${height}/0/`,
                {
                    method: "POST",
                    headers: { Accept: "image/png", "Content-Type": "application/x-www-form-urlencoded" },
                    body: zpl
                });

            if (!resp.ok) throw new Error(`Label preview service returned ${resp.status}`);

            const objectUrl = URL.createObjectURL(await resp.blob());

            if (renderSeq !== this.previewRenderSeq) {
                URL.revokeObjectURL(objectUrl);
                return;
            }

            // Swapped only once the new image is ready, so the previous one stays visible
            // while a re-render is in flight.
            this.revokePreviewImage();
            this.previewImageUrl = objectUrl;
            this.previewImageError = false;
        } catch (err) {
            if (renderSeq !== this.previewRenderSeq) return;

            console.error("Failed to render label preview", err);
            this.revokePreviewImage();
            this.previewImageError = true;
        }
    }

    revokePreviewImage() {
        if (!this.previewImageUrl) return;
        URL.revokeObjectURL(this.previewImageUrl);
        this.previewImageUrl = "";
    }

    async printPreview() {
        if (!this.activeLocalPrinter) {
            this.$notification(NotificationOptions.error("Select a label printer before printing."));
            return;
        }

        this.printing = true;
        try {
            // The ZPL to print is whatever the preview endpoint returns, so print generates it on
            // demand instead of making the user click Preview Label first. Previewing also shows
            // what was sent, which is why printing renders the image rather than fetching quietly.
            if (!this.previewContent?.content) await this.fetchPreview();

            const zpl = this.previewContent?.content;
            if (!zpl) throw new Error("No label could be generated for the current settings.");

            await this.sendRawPrint({
                printerName: this.activeLocalPrinter.name,
                rawContent: zpl,
                documentName: `label-template-test-${this.previewContent?.template?.name ?? "preview"}`,
                copies: 1
            });

            this.$notification(NotificationOptions.success("Label sent to printer."));
        } catch (err) {
            console.error("Failed to print label", err);
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.printing = false;
        }
    }

    /**
     * Prints through the shared worker's client service connection, falling back to the local HTTP
     * endpoint when the worker answers that it couldn't.
     *
     * Resolves only once a print has actually been accepted. The worker drops raw prints whenever
     * its client service hub is disconnected, so treating a posted message as a printed label
     * reports success for labels that never reach the printer — and because a tab always has a
     * worker port, that also left the HTTP fallback unreachable.
     */
    private async sendRawPrint(request: RawPrintRequest) {
        // Correlates this print with its reply, generated here so no caller can omit it.
        const job: RawPrintRequest = { ...request, jobId: PrintService.generateJobId() };

        if (!this.hasWorkerPort) {
            await PrintService.sendRawPrint(job);
            return;
        }

        const ack = await this.requestFromWorker<RawPrintAck | null>(
            "clientServicePrintRaw", "clientServicePrintRawResponse", null, {
                payload: job,
                matches: reply => reply?.jobId === job.jobId,
                timeoutMs: printAckTimeoutMs
            });

        if (ack?.success) return;

        // Silence leaves the job's fate unknown and is the one case not worth retrying: a reply
        // always comes back within a client service call, so waiting longer beats a duplicate label.
        if (!ack) {
            throw new Error("The Scriptly Client Service did not confirm the print. "
                + "Check that it is running, then try again.");
        }

        // The worker asks for a fallback whenever it couldn't hand the job over — including the
        // disconnected hub behind the false successes this replaced, and payloads its SignalR
        // connection refuses but the HTTP endpoint accepts.
        if (!ack.shouldFallback) throw new Error(ack.error || "The label could not be printed.");

        await PrintService.sendRawPrint(job);
    }

    async getSupportedClientService() {
        // The worker holds an already-open connection; the direct call is the fallback when this
        // tab has no worker port. Both report absence rather than throwing.
        const version = await this.requestFromWorker<string | null>(
            "clientServiceRefreshVersion", "clientServiceVersion", null)
            ?? await PrintService.getVersion();

        this.hasSupportedClientService = PrintService.isSupportedVersion(version);
    }

    async getLocalPrinters() {
        const printers = await this.requestFromWorker<PrintDevice[]>(
            "clientServiceRefreshPrinters", "clientServicePrinters", []);

        this.setLocalPrinters(printers.length ? printers : await PrintService.getDevices());
    }

    private setLocalPrinters(printers: PrintDevice[]) {
        // Surface ZPL/Zebra printers first; they're the ones that can render a label template.
        this.localPrinters = PrintService.sortZplFirst(printers);

        const lastPrinter = localStorage.getItem(printerStorageKey);
        this.activeLocalPrinter = this.localPrinters.find(p => p.name === lastPrinter)
            ?? this.localPrinters[0]
            ?? null;
    }

    prettyPrinterName(printer: PrintDevice | null) {
        return PrintService.prettyPrinterName(printer, "No Printer Defined");
    }

    @Watch("activeLocalPrinter")
    activeLocalPrinterChanged() {
        if (!this.activeLocalPrinter?.name) return;
        localStorage.setItem(printerStorageKey, this.activeLocalPrinter.name);
    }

    /**
     * Switches the preview to the resolution a template declares it was authored for. Rendering is
     * left to the caller: adopting a DPI accompanies picking a template, and both are covered by
     * the render that picking one leads to.
     */
    private adoptTemplateDpi(dpi?: number) {
        if (!dpi || dpi === this.previewDpi) return;
        this.adoptingTemplateDpi = true;
        this.previewDpi = dpi;
    }

    @Watch("previewDpi")
    previewDpiChanged() {
        localStorage.setItem(dpiStorageKey, String(this.previewDpi));

        // Cleared here rather than at the assignment because the watcher runs a tick later.
        const adopted = this.adoptingTemplateDpi;
        this.adoptingTemplateDpi = false;

        // DPI drives server-side rasterization of scalable text, so the ZPL has to be rebuilt.
        if (!adopted && this.previewContent) this.getPreview();
    }

    @Watch("previewTemplate.templateId")
    previewTemplateIdChanged(templateId: number | null) {
        this.adoptTemplateDpi(this.templates.find(t => t.id === templateId)?.dpi);
    }

    @Watch("selectedLabelSizeKey")
    selectedLabelSizeChanged(sizeKey: string) {
        const { width, height } = this.selectedLabelSize;
        if (!(width > 0) || !(height > 0)) return;

        localStorage.setItem(labelSizeStorageKey, sizeKey);
        // Size only changes how the existing ZPL is rendered, so no need to re-fetch it.
        if (this.previewContent) this.renderPreviewImage();
    }

    async getDefaultTemplate() {
        if (!this.activeTemplate) return;

        try {
            const resp = await this.$http.get<string>("/LabelTemplate/default", {
                params: {
                    type: this.activeTemplate.type,
                    // Sends the template's own resolution so the starting point is already drawn in
                    // that resolution's dot space. Without it a 203 DPI template would open with a
                    // 300 DPI drawing whose every coordinate has to be rescaled by hand.
                    dpi: this.activeTemplate.dpi
                }
            })
            this.activeTemplate.template = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    get templateOptions() {
        const filteredTemplates = this.templates.filter(t => t.type === this.previewTemplate.type)
            .map(t => ({ value: t.id, text: t.name }));
        return [{ value: null, text: "" }, ...filteredTemplates];
    }

    @Watch("previewTemplate.type")
    previewTemplateTypeChange() {
        this.previewTemplate.templateId = null;
    }

    addToken(tkn: string) {
        const currentValue = this.activeTemplate!.template;
        const textArea = this.templateZpl.$el;
        const cursorStartPosition = textArea?.selectionStart ?? currentValue.length;
        const cursorEndPosition = textArea?.selectionEnd ?? currentValue.length;
        const newValue = `${currentValue.slice(0, cursorStartPosition)}{{${tkn}}}${currentValue.slice(cursorEndPosition)}`;
        this.activeTemplate!.template = newValue;

        this.$nextTick(() => {
            setTimeout(() => {
                const newPosition = cursorStartPosition + tkn.length + 4;
                textArea.selectionStart = newPosition;
                textArea.selectionEnd = newPosition;
                textArea.focus();
            }, 100)
        });
    }
}

</script>

<style scoped>
/* Sized to the ZPL editor specifically; a bare `textarea` selector also stretched the
   tester's Directions input to full viewport height. */
::v-deep(.zpl-editor) {
    height: calc(100vh - 305px) !important;
    max-height: calc(100vh - 305px) !important;
}

/* The controls column runs far longer than the preview, so the rendered label scrolls out of
   view while its own settings are still being adjusted. Pin it only from the breakpoint where
   the two columns sit side by side; below that they stack and there is nothing to pin against.
   align-self keeps the column from stretching to the row's full height — a sticky box that
   already spans the scroll area never has anywhere to stick. */
@media (min-width: 1200px) {
    .preview-pane {
        position: sticky;
        top: 0;
        align-self: flex-start;
    }
}
</style>
